import React from 'react';
import { useTranslation } from 'react-i18next';
import { useSectionScroll, useScrollToTop } from '../hooks/useSectionScroll';
import {
  Box,
  Container,
  Typography,
  Grid,
  Paper,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  CssBaseline,
  Card,
  CardContent,
} from '@mui/material';
import NavigationBar from '../components/layout/NavigationBar';
import Footer from '../components/layout/Footer';
import SecurityIcon from '@mui/icons-material/Security';
import VerifiedUserIcon from '@mui/icons-material/VerifiedUser';
import LockIcon from '@mui/icons-material/Lock';
import GavelIcon from '@mui/icons-material/Gavel';
import StorageIcon from '@mui/icons-material/Storage';
import PrivacyTipIcon from '@mui/icons-material/PrivacyTip';
import PolicyIcon from '@mui/icons-material/Policy';
import ShieldIcon from '@mui/icons-material/Shield';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';

const colors = {
  black: '#000000',
  darkRed: '#451a15',
  red: '#970000',
  yellow: '#fdb200',
  offWhite: '#f6ede7',
};

function Security() {
  const { t } = useTranslation();

  useScrollToTop();
  useSectionScroll();

  const securityPrinciples = [
    {
      icon: <LockIcon />,
      title: t('security.principles.encryption.title'),
      description: t('security.principles.encryption.description'),
    },
    {
      icon: <PrivacyTipIcon />,
      title: t('security.principles.privacy.title'),
      description: t('security.principles.privacy.description'),
    },
    {
      icon: <StorageIcon />,
      title: t('security.principles.dataMinimization.title'),
      description: t('security.principles.dataMinimization.description'),
    },
    {
      icon: <VerifiedUserIcon />,
      title: t('security.principles.access.title'),
      description: t('security.principles.access.description'),
    },
  ];

  const complianceItems = [
    {
      title: t('security.gdpr.title'),
      icon: <GavelIcon />,
      points: [
        t('security.gdpr.lawfulBasis'),
        t('security.gdpr.dataSubjectRights'),
        t('security.gdpr.privacyByDesign'),
        t('security.gdpr.dataProtection'),
      ],
    },
    {
      title: t('security.aiAct.title'),
      icon: <PolicyIcon />,
      points: [
        t('security.aiAct.transparency'),
        t('security.aiAct.humanOversight'),
        t('security.aiAct.riskAssessment'),
        t('security.aiAct.documentation'),
      ],
    },
  ];

  const securityPractices = [
    t('security.practices.dataLocation'),
    t('security.practices.monitoring'),
    t('security.practices.incidentResponse'),
    t('security.practices.vendorManagement'),
    t('security.practices.employeeTraining'),
    t('security.practices.regularAudits'),
  ];

  return (
    <>
      <CssBaseline />
      <Box sx={{ backgroundColor: colors.offWhite, minHeight: '100vh' }}>
        <NavigationBar />

        {/* Spacer for fixed navbar */}
        <Box sx={{ height: '64px' }} />

        {/* Hero Section */}
        <Box
          sx={{
            backgroundColor: colors.yellow,
            py: 8,
            textAlign: 'center',
            borderBottom: `2px solid ${colors.red}`,
          }}
        >
          <Container maxWidth="lg">
            <Box sx={{ display: 'flex', justifyContent: 'center', mb: 3 }}>
              <SecurityIcon sx={{ fontSize: 100, color: colors.red }} />
            </Box>
            <Typography
              variant="h2"
              sx={{
                color: colors.darkRed,
                fontWeight: 'bold',
                mb: 2,
                fontSize: { xs: '2.5rem', md: '3.5rem' },
              }}
            >
              {t('security.hero.title')}
            </Typography>
            <Typography
              variant="h5"
              sx={{
                color: colors.darkRed,
                mb: 4,
                fontWeight: 300,
                lineHeight: 1.6,
                maxWidth: '800px',
                mx: 'auto',
              }}
            >
              {t('security.hero.subtitle')}
            </Typography>
          </Container>
        </Box>

        {/* Key Commitment Section */}
        <Container maxWidth="lg" sx={{ py: 6 }}>
          <Paper
            elevation={3}
            sx={{
              p: 4,
              backgroundColor: 'white',
              borderRadius: 3,
              borderTop: `4px solid ${colors.red}`,
            }}
          >
            <Grid container alignItems="center" spacing={3}>
              <Grid item xs={12} md={2} sx={{ textAlign: 'center' }}>
                <ShieldIcon sx={{ fontSize: 80, color: colors.red }} />
              </Grid>
              <Grid item xs={12} md={10}>
                <Typography
                  variant="h4"
                  sx={{
                    color: colors.darkRed,
                    fontWeight: 'bold',
                    mb: 2,
                  }}
                >
                  {t('security.commitment.title')}
                </Typography>
                <Typography
                  variant="h6"
                  sx={{
                    color: colors.darkRed,
                    lineHeight: 1.8,
                  }}
                >
                  {t('security.commitment.description')}
                </Typography>
              </Grid>
            </Grid>
          </Paper>
        </Container>

        {/* Security Principles */}
        <Container maxWidth="lg" sx={{ py: 6 }}>
          <Typography
            variant="h3"
            sx={{
              color: colors.darkRed,
              fontWeight: 'bold',
              mb: 4,
              textAlign: 'center',
            }}
          >
            {t('security.principles.title')}
          </Typography>
          <Grid container spacing={3}>
            {securityPrinciples.map((principle, index) => (
              <Grid item xs={12} md={6} key={index}>
                <Card
                  sx={{
                    height: '100%',
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      transform: 'translateY(-4px)',
                      boxShadow: '0 8px 24px rgba(0,0,0,0.15)',
                    },
                  }}
                >
                  <CardContent sx={{ p: 3 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                      <Box
                        sx={{
                          backgroundColor: colors.yellow,
                          borderRadius: '50%',
                          p: 1.5,
                          mr: 2,
                        }}
                      >
                        {React.cloneElement(principle.icon, {
                          sx: { color: colors.darkRed, fontSize: 30 },
                        })}
                      </Box>
                      <Typography
                        variant="h5"
                        sx={{
                          color: colors.darkRed,
                          fontWeight: 'bold',
                        }}
                      >
                        {principle.title}
                      </Typography>
                    </Box>
                    <Typography
                      variant="body1"
                      sx={{
                        color: colors.darkRed,
                        lineHeight: 1.6,
                      }}
                    >
                      {principle.description}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Container>

        {/* Compliance Section */}
        <Box sx={{ backgroundColor: 'white', py: 6 }}>
          <Container maxWidth="lg">
            <Typography
              variant="h3"
              sx={{
                color: colors.darkRed,
                fontWeight: 'bold',
                mb: 4,
                textAlign: 'center',
              }}
            >
              {t('security.compliance.title')}
            </Typography>
            <Grid container spacing={4}>
              {complianceItems.map((item, index) => (
                <Grid item xs={12} md={6} key={index}>
                  <Paper
                    elevation={3}
                    sx={{
                      p: 4,
                      height: '100%',
                      borderTop: `4px solid ${colors.red}`,
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                      {React.cloneElement(item.icon, {
                        sx: { color: colors.red, fontSize: 40, mr: 2 },
                      })}
                      <Typography
                        variant="h4"
                        sx={{
                          color: colors.darkRed,
                          fontWeight: 'bold',
                        }}
                      >
                        {item.title}
                      </Typography>
                    </Box>
                    <List>
                      {item.points.map((point, pointIndex) => (
                        <ListItem key={pointIndex} sx={{ pl: 0 }}>
                          <ListItemIcon sx={{ minWidth: 36 }}>
                            <CheckCircleOutlineIcon
                              sx={{ color: colors.yellow, fontSize: 24 }}
                            />
                          </ListItemIcon>
                          <ListItemText
                            primary={point}
                            primaryTypographyProps={{
                              sx: { color: colors.darkRed, fontSize: '1rem' },
                            }}
                          />
                        </ListItem>
                      ))}
                    </List>
                  </Paper>
                </Grid>
              ))}
            </Grid>
          </Container>
        </Box>

        {/* Security Practices */}
        <Container maxWidth="lg" sx={{ py: 6 }}>
          <Typography
            variant="h3"
            sx={{
              color: colors.darkRed,
              fontWeight: 'bold',
              mb: 4,
              textAlign: 'center',
            }}
          >
            {t('security.practices.title')}
          </Typography>
          <Paper
            elevation={2}
            sx={{
              p: 4,
              backgroundColor: 'white',
            }}
          >
            <Typography
              variant="body1"
              sx={{
                color: colors.darkRed,
                mb: 3,
                fontSize: '1.1rem',
                lineHeight: 1.7,
              }}
            >
              {t('security.practices.intro')}
            </Typography>
            <Grid container spacing={2}>
              {securityPractices.map((practice, index) => (
                <Grid item xs={12} md={6} key={index}>
                  <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 2 }}>
                    <CheckCircleOutlineIcon
                      sx={{
                        color: colors.yellow,
                        fontSize: 24,
                        mr: 2,
                        mt: 0.5,
                      }}
                    />
                    <Typography
                      variant="body1"
                      sx={{
                        color: colors.darkRed,
                        lineHeight: 1.6,
                      }}
                    >
                      {practice}
                    </Typography>
                  </Box>
                </Grid>
              ))}
            </Grid>
          </Paper>
        </Container>

        {/* Contact Section */}
        <Box sx={{ backgroundColor: colors.yellow, py: 6 }}>
          <Container maxWidth="lg">
            <Paper
              elevation={0}
              sx={{
                p: 4,
                backgroundColor: 'white',
                borderRadius: 3,
                textAlign: 'center',
              }}
            >
              <Typography
                variant="h4"
                sx={{
                  color: colors.darkRed,
                  fontWeight: 'bold',
                  mb: 2,
                }}
              >
                {t('security.contact.title')}
              </Typography>
              <Typography
                variant="body1"
                sx={{
                  color: colors.darkRed,
                  mb: 3,
                  fontSize: '1.1rem',
                }}
              >
                {t('security.contact.description')}
              </Typography>
              <Typography
                variant="h6"
                sx={{
                  color: colors.red,
                  fontWeight: 'bold',
                }}
              >
                info@ulpian.com
              </Typography>
            </Paper>
          </Container>
        </Box>

        <Footer />
      </Box>
    </>
  );
}

export default Security;
